import { useState } from 'react'

import { Button, Portal } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import type { CompanyListItem } from '@/shared/types/domain'

import type { SettingsUser } from '../types'

export interface UserCompaniesSheetProps {
  user: SettingsUser
  companies: readonly CompanyListItem[]
  pending?: boolean
  onCancel: () => void
  onSave: (companyIds: string[]) => void
}

/**
 * A qué empresas ve un `tech_member`.
 *
 * Se guarda el conjunto completo, no un delta: la pantalla edita una lista de
 * checkboxes y mandar el estado final evita la ventana en la que el usuario
 * quedó con una empresa de más entre dos requests.
 */
export function UserCompaniesSheet({
  user,
  companies,
  pending,
  onCancel,
  onSave,
}: UserCompaniesSheetProps) {
  const t = useT()
  // `company_ids` viene `null` cuando el backend no informa las membresías.
  // La pantalla ya no abre este panel en ese caso; el `?? []` es la red.
  const [selected, setSelected] = useState<string[]>(user.company_ids ?? [])

  const toggle = (companyId: string) => {
    setSelected((current) =>
      current.includes(companyId)
        ? current.filter((id) => id !== companyId)
        : [...current, companyId],
    )
  }

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg/70 p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('settings.users.companiesAria', { persona: user.display_name })}
          className="hairline flex w-full max-w-[420px] flex-col gap-3 rounded-[var(--radius-card)] bg-surface-1 p-4"
        >
          <header className="flex flex-col gap-1">
            <h2 className="font-display text-base text-text-1">
              {t('settings.users.companiesAria', { persona: user.display_name })}
            </h2>
            <p className="text-2xs text-text-3">{t('settings.users.companiesHint')}</p>
          </header>

          <ul className="flex flex-col gap-1">
            {companies.map((company) => (
              <li key={company.id}>
                <label className="flex cursor-pointer items-center gap-2 rounded-[var(--radius-control)] px-1.5 py-1.5 text-xs hover:bg-surface-2">
                  <input
                    type="checkbox"
                    checked={selected.includes(company.id)}
                    onChange={() => toggle(company.id)}
                  />
                  <span
                    aria-hidden="true"
                    className="h-3.5 w-[var(--spine-w)] rounded-full"
                    style={{ backgroundColor: `hsl(${String(company.color_hue)} 62% 55%)` }}
                  />
                  <span className="text-text-1">{company.name}</span>
                </label>
              </li>
            ))}
          </ul>

          <div className="flex justify-end gap-2">
            <Button onClick={onCancel}>{t('common.action.cancel')}</Button>
            <Button variant="primary" disabled={pending} onClick={() => onSave(selected)}>
              {t('common.action.save')}
            </Button>
          </div>
        </div>
      </div>
    </Portal>
  )
}
